import React, { useEffect, useMemo, useState } from 'react';
import styled from 'styled-components';
import { Trans, t } from '@lingui/macro';
import { Modal, SelectionBox, TextField, MODAL_SIZE } from '@toqio/toqio-web-components';
import type { Bank } from '../schema';

type Props = {
  open: boolean;
  banks: Bank[];
  selectedId?: string;
  onSelect: (bank: Bank) => void;
  onClose: () => void;
};

const Body = styled.div`
  display: flex;
  flex-direction: column;
  gap: 12px;
  max-height: 60vh;
`;

const List = styled.div`
  display: flex;
  flex-direction: column;
  gap: 8px;
  overflow-y: auto;
`;

const Empty = styled.div`
  padding: 24px 0;
  text-align: center;
  color: ${({ theme }) => (theme.colors?.placeholders_box_text_color as string | undefined) ?? '#888'};
`;

const initialsOf = (name: string): string => {
  const trimmed = name.trim();
  if (!trimmed) return '?';
  return trimmed.slice(0, 2).toUpperCase();
};

/**
 * Bank picker — Flowable's PISP_F001 modal.
 *
 * Pure UI component: the parent supplies the bank list and the selection
 * callback. Search is local ephemeral state — not a form field — and
 * resets when the modal closes. Empty search shows every bank (we
 * deliberately drop the legacy Flowable quirk where clearing the search
 * did NOT restore the list — see PRD § 11 O-3).
 */
export const BankPickerModal: React.FC<Props> = ({
  open,
  banks,
  selectedId,
  onSelect,
  onClose,
}) => {
  const [query, setQuery] = useState('');

  useEffect(() => {
    if (!open) setQuery('');
  }, [open]);

  const filtered = useMemo(() => {
    const q = query.trim().toLowerCase();
    if (!q) return banks;
    return banks.filter((b) => b.name.toLowerCase().includes(q));
  }, [banks, query]);

  return (
    <Modal
      title={t`Select a bank`}
      visible={open}
      close={onClose}
      size={MODAL_SIZE.S}
      stretch="normal"
    >
      <Body>
        <TextField
          placeholder={t`Start typing`}
          style={{ width: '100%' }}
          value={query}
          onChange={(value: string) => setQuery(value)}
          id="external-bank-picker-search"
          htmlFor="external-bank-picker-search"
        />
        <List>
          {filtered.length === 0 ? (
            <Empty>
              <Trans>No banks match your search</Trans>
            </Empty>
          ) : (
            filtered.map((bank) => (
              <SelectionBox
                key={bank.id}
                title={bank.name}
                description={bank.alternativeName ?? ''}
                avatar={{ image: bank.logoUrl, initials: initialsOf(bank.name) }}
                selected={bank.id === selectedId}
                onClick={() => onSelect(bank)}
              />
            ))
          )}
        </List>
      </Body>
    </Modal>
  );
};
